import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { AnimatePresence, motion } from "framer-motion";
import { LifeBuoy, Menu, Moon, Sun, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SupportModal } from "@/components/SupportModal";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/who-we-help", label: "Who We Help" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-200 hover:text-foreground ${
      isActive ? "text-foreground" : "text-muted-foreground"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" aria-label="Groundbreaking IT home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase()}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSupportOpen(true)}
              data-testid="client-support-button"
              className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 px-3 py-2"
            >
              <LifeBuoy className="h-4 w-4" />
              Client Support
            </button>
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              className="grid h-9 w-9 place-items-center rounded-full border border-border text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              <Sun className="h-4 w-4 dark:hidden" />
              <Moon className="h-4 w-4 hidden dark:block" />
            </button>
            <Link
              to="/contact?intent=review"
              data-testid="nav-review-cta"
              className="hidden sm:inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-primary/25 active:scale-[0.98]"
            >
              Free IT Review
            </Link>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
              className="lg:hidden grid h-9 w-9 place-items-center rounded-full border border-border"
            >
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden border-t border-border bg-background"
            aria-label="Mobile"
          >
            <div className="px-4 py-4 space-y-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-muted"
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => { setSupportOpen(true); setMenuOpen(false); }}
                data-testid="mobile-support-button"
                className="block w-full text-left rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-muted"
              >
                Client Support
              </button>
              <Link
                to="/contact?intent=review"
                data-testid="mobile-review-cta"
                className="mt-2 block rounded-full bg-primary px-5 py-3 text-center text-sm font-semibold text-primary-foreground"
              >
                Request a Free IT Review
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
    </header>
  );
};
